import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { toast } from 'react-toastify';
import { CheckCircle2, Loader2 } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useFormButtonText } from '@/hooks/useFormButtonText';
import { useUser } from '@/hooks/useUser';
import { useMutationsUpdateNotificationPreferences } from '@/reactQuery/post';
import ToastMessage from '../toast-message/ToastMessage';

const defaultValues = {
  first_response: true,
  all_responses_collected: true,
  analysis_complete: true,
};

const validationSchema = Yup.object().shape({
  first_response: Yup.boolean().required(),
  all_responses_collected: Yup.boolean().required(),
  analysis_complete: Yup.boolean().required(),
});

type TNotificationPreferences = typeof defaultValues;

function NotificationPreferencesForm() {
  const { user } = useUser();
  const { lang } = useTranslation();
  const initial = useRef(true);
  const { mutateAsync, status: mutationStatus } = useMutationsUpdateNotificationPreferences();
  const [isAllSaved, setIsAllSaved] = useState<boolean>(false);
  const [isLocalSaving, setIsLocalSaving] = useState(false);
  const { currentTheme } = useTheme();
  const { getButtonText } = useFormButtonText();
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { isSubmitting, dirtyFields, isDirty },
  } = useForm<TNotificationPreferences>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'all',
  });

  useEffect(() => {
    if (user?.notification_preferences) {
      reset({
        first_response: user.notification_preferences.first_response ?? true,
        all_responses_collected: user.notification_preferences.all_responses_collected ?? true,
        analysis_complete: user.notification_preferences.analysis_complete ?? true,
      });
    }
  }, [reset, user]);

  const onSubmit = async (data: TNotificationPreferences) => {
    setIsLocalSaving(true);

    try {
      await mutateAsync(data);

      toast.success(
        <ToastMessage
          title="notificationPreferencesUpdated"
          description="notificationPreferencesUpdatedDescription"
        />,
      );
      reset({ ...data });
      initial.current = false;
      setIsAllSaved(true);
    } catch {
      toast.error(lang.get('msg.unableToUpdateProfile'));
    } finally {
      setIsLocalSaving(false);
    }
  };

  const hasChangesInForm =
    Object.keys(dirtyFields).length > 0 || (!isAllSaved && (!initial.current || isDirty));
  const isButtonDisabled = isSubmitting || isLocalSaving || !hasChangesInForm;

  const buttonText = getButtonText({
    status: mutationStatus,
    isSubmitting: isSubmitting || isLocalSaving,
    hasChanges: hasChangesInForm,
    labels: {
      saveChanges: 'msg.saveNotificationPreferences',
    },
  });

  const isPending = mutationStatus === 'pending';
  const isSuccess = mutationStatus === 'success';

  const toggleRows = [
    {
      name: 'first_response' as const,
      label: lang.get('msg.notificationFirstResponse'),
      description: lang.get('msg.notificationFirstResponseDescription'),
    },
    {
      name: 'all_responses_collected' as const,
      label: lang.get('msg.notificationAllResponsesCollected'),
      description: lang.get('msg.notificationAllResponsesCollectedDescription'),
    },
    {
      name: 'analysis_complete' as const,
      label: lang.get('msg.notificationAnalysisComplete'),
      description: lang.get('msg.notificationAnalysisCompleteDescription'),
    },
  ];

  return (
    <>
      <h2 className="text-lg font-semibold mt-10">{lang.get('msg.emailNotifications')}</h2>
      <p
        style={{ color: currentTheme?.['secondary-text'] }}
        className="mt-1 mb-6 text-sm text-light-gray"
      >
        {lang.get('msg.emailNotificationsDescription')}
      </p>
      <hr />
      <form
        className="h-full max-w-lg mt-5 flex flex-col items-start"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col w-full gap-4">
          {toggleRows.map((row) => (
            <div key={row.name} className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <span
                  style={{ color: currentTheme?.['primary-text'] }}
                  className="text-sm font-semibold"
                >
                  {row.label}
                </span>
                <span
                  style={{ color: currentTheme?.['secondary-text'] }}
                  className="text-xs text-light-gray"
                >
                  {row.description}
                </span>
              </div>
              <Controller
                name={row.name}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch checked={value} onCheckedChange={onChange} />
                )}
              />
            </div>
          ))}
        </div>

        <p
          style={{ color: currentTheme?.['secondary-text'] }}
          className="mt-4 text-xs text-light-gray"
        >
          {lang.get('msg.notificationPreferencesNote')}
        </p>

        <Button variant="default" disabled={isButtonDisabled} className="mt-6">
          {(isPending || isLocalSaving) && (
            <Loader2 className="mr-1.5 animate-spin" size={20} />
          )}
          {isAllSaved && isSuccess && !hasChangesInForm && !isLocalSaving && (
            <CheckCircle2 className="mr-1.5" size={20} />
          )}
          {buttonText}
        </Button>
      </form>
    </>
  );
}

export default NotificationPreferencesForm;
